import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  effect,
  inject,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { EventType, NavigationStart, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import {
  InitializeAzureSSOFlow,
  SetCurrentlyReplacingAzureTokens,
} from '@wilson/auth/core';
import { FeatureFlagsService } from '@wilson/feature-flags';
import { LaunchDarklyUserKeys } from '@wilson/interfaces';
import { TitleManagerService } from '@wilson/title-manager';
import { NzIconService } from 'ng-zorro-antd/icon';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { firstValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { TabVisibilityService } from '@wilson/utils';
import { SentryRouterTrackerService } from '@wilson/monitoring/sentry';
import {
  DEFAULT_LANGUAGE_TYPE,
  LocalizationState,
  SHOW_TRANSLATION_KEY,
  SUPPORTED_LANGUAGE_TYPES,
} from '@wilson/localization';
import { select } from '@ngxs/store';

@Component({
  selector: 'wilson-root',
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly translateService = inject(TranslateService);
  private readonly featureFlags = inject(FeatureFlagsService);
  private readonly titleManager = inject(TitleManagerService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly notification = inject(NzNotificationService);
  private readonly tabVisibilityService = inject(TabVisibilityService);
  private readonly sentryRouterTracker = inject(SentryRouterTrackerService);
  private readonly nzIconService = inject(NzIconService);

  private readonly lang = select(LocalizationState.getLang);

  constructor() {
    this.nzIconService.changeAssetsSource('');
    effect(() => {
      const lang = this.lang();
      this.document.documentElement.lang =
        lang !== SHOW_TRANSLATION_KEY ? lang : DEFAULT_LANGUAGE_TYPE;
    });
  }

  ngOnInit(): void {
    // Initialize router tracking
    this.sentryRouterTracker.init();

    this.translateService.addLangs(
      SUPPORTED_LANGUAGE_TYPES as unknown as string[],
    );

    this.featureFlags.initialize(LaunchDarklyUserKeys.WilsonPortal);
    this.titleManager.init();

    this.addAzureAuthListener();
    console.info(`Version: `, environment.releaseVersion);
    this.tabVisibilityService.startTabVisibilityListener();
  }

  /**
   * Handles Azure SSO for web portal.
   * - Listens for specific NavigationStart events with 'accessToken' and 'refreshToken'.
   * - Dispatches action and validates the extracted tokens.
   * - Invokes SSO flow and navigates to home, or shows a notification on failure.
   * Note: Uses URLSearchParams due to router not giving us the full URL
   */

  private addAzureAuthListener(): void {
    this.router.events
      .pipe(
        filter(
          (event: NavigationStart) =>
            event.type === EventType.NavigationStart &&
            event.url &&
            ((event.url.includes('accessToken') &&
              event.url.includes('refreshToken')) ||
              (event.url.includes('azureErrorInternal') &&
                event.url.includes('azureErrorExternal'))),
        ),
        take(1),
      )
      .subscribe(async (event) => {
        const urlSearchParams = new URLSearchParams(event.url.split('?')[1]);
        try {
          this.store.dispatch(new SetCurrentlyReplacingAzureTokens(true));
          const accessToken = urlSearchParams.get('accessToken');
          const refreshToken = urlSearchParams.get('refreshToken');
          if (!accessToken || !refreshToken) {
            throw new Error('Invalid access or refreshtoken!');
          }
          await firstValueFrom(
            this.store.dispatch(
              new InitializeAzureSSOFlow(accessToken, refreshToken),
            ),
          );
          this.router.navigate(['/']);
        } catch (e) {
          this.store.dispatch(new SetCurrentlyReplacingAzureTokens(false));
          const azureErrorInternal = urlSearchParams.get('azureErrorInternal');
          const azureErrorExternal = urlSearchParams.get('azureErrorExternal');
          console.error('azureError:', azureErrorInternal, azureErrorExternal);
          this.notification.error(
            this.translateService.instant('auth.azure_sso_failed.title'),
            `${this.translateService.instant(
              'auth.azure_sso_failed.content',
            )} ${azureErrorExternal}`,
          );
        }
      });
  }

  ngOnDestroy(): void {
    this.tabVisibilityService.stopTabVisibilityListener();
    this.sentryRouterTracker.clearSubscriptions();
  }
}
